<!-- BPMN overlay card shown below tasks that interact with the focused entity. -->
<!-- Displays the interaction label, entity name, and linked application names. -->
<template>
    <div v-if="entityRef" class="entity-focus-annotation">
        <span class="entity-focus-annotation__label">
            {{ entityRef.label }} {{ entityName }}
        </span>
        <span
            v-if="applicationNames.length > 0"
            class="entity-focus-annotation__apps"
        >
            {{ applicationNames.join(", ") }}
        </span>
    </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import { isLinkedToTask } from "@/components/Applications/task-links";
import { focusedEntityId } from "@/composables/bpmn-manager/entity-focus";
import { useApplicationsManager } from "@/stores/applications-store";
import { useBusinessEntitiesManager } from "@/stores/business-entities-store";
import { useProcessElementsManager } from "@/stores/process-elements-store";

const props = defineProps<{
    elementId: string;
    processElementId?: string;
}>();

const { processElements } = useProcessElementsManager();
const { entities } = useBusinessEntitiesManager();
const { applications } = useApplicationsManager();

const processElement = computed(() =>
    processElements.value?.find(
        (p) =>
            p.identity === props.processElementId ||
            p.bpmn_id === props.elementId
    )
);

const entityRef = computed(() => {
    if (!focusedEntityId.value) return undefined;
    return processElement.value?.entity_refs?.find(
        (r) => r.entity_id === focusedEntityId.value
    );
});

const entityName = computed(
    () =>
        entities.value?.find((e) => e.identity === focusedEntityId.value)
            ?.name ??
        focusedEntityId.value ??
        ""
);

const applicationNames = computed(() => {
    const taskId = processElement.value?.identity;
    if (!taskId) return [];
    return applications.value
        .filter((a) => isLinkedToTask(a, taskId))
        .map((a) => a.name);
});
</script>

<style scoped>
.entity-focus-annotation {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    background: var(--stekz-linkedentities-color);
    /* border: 1px solid var(--stekz-outline); */
    padding: 2px 6px;
    font-size: 10px;
    white-space: nowrap;
    gap: 1px;
    pointer-events: none;
}

.entity-focus-annotation__label {
    font-weight: 500;
    color: var(--stekz-on-surface);
}

.entity-focus-annotation__apps {
    color: var(--stekz-on-surface);
}
</style>
